<template>
  <ShareModes
    :modes="modes"
    class="instagram-section"
  >
    <template #story>
      <ShareSteps>
        <ShareStep
          :number="1"
          :title="$t('instagram_download_image_title')"
          :description="$t('instagram_download_image_description')"
        >
          <ShareImageDownload
            :media="media"
            :is-loading="isMediaLoading"
          />
        </ShareStep>

        <ShareStep
          :number="2"
          :title="$t('instagram_copy_link_title')"
          :description="$t('instagram_copy_link_description')"
        >
          <ShareCopyLink
            :landing-page-url="landingPageUrl"
            open-href="https://www.instagram.com"
            :open-label="$t('open_instagram')"
          />

          <div class="mt-8">
            <Accordion v-model="accordion">
              <AccordionItem>
                <template #title>
                  <div class="flex items-center space-x-3 text-sm">
                    <Icon
                      icon="bullhorn"
                      prefix="far"
                    />
                    <span>{{ $t('want_custom_sticker_text') }}</span>
                  </div>
                </template>

                <AiDraft
                  :key="language"
                  v-model="stickerText"
                  :endpoint="`${aiEndpoint}/${language}/instagram-custom-sticker`"
                  :body="aiBody"
                  :language="language"
                  :partner-name="partnerName"
                  class="shadow"
                  :replacements="{ '[link]': landingPageUrl }"
                  @status="(value) => (stickerTextStatus = value)"
                />

                <div class="mt-6 flex flex-wrap items-center gap-3">
                  <ShareCopyButton
                    :source="stickerText"
                    :label="$t('copy_text')"
                    :disabled="['pending', 'error'].includes(stickerTextStatus)"
                  />
                  <ShareOpenLink
                    href="https://www.instagram.com"
                    :label="$t('open_instagram')"
                  />
                </div>
              </AccordionItem>

              <AccordionItem>
                <template #title>
                  <div class="flex items-center space-x-3 text-sm">
                    <Icon
                      icon="message-captions"
                      prefix="far"
                    />
                    <span>{{ $t('want_a_caption') }}</span>
                  </div>
                </template>

                <AiDraft
                  :key="language"
                  v-model="caption"
                  :endpoint="`${aiEndpoint}/${language}/instagram`"
                  :body="aiBody"
                  :language="language"
                  :partner-name="partnerName"
                  :initial-text="initialText"
                  class="shadow"
                  :replacements="{ '[link]': landingPageUrl }"
                  @status="(value) => (captionStatus = value)"
                />

                <div class="mt-6 flex flex-wrap items-center gap-3">
                  <ShareCopyButton
                    :source="caption"
                    :label="$t('copy_caption')"
                    :disabled="['pending', 'error'].includes(captionStatus)"
                  />
                  <ShareOpenLink
                    href="https://www.instagram.com"
                    :label="$t('open_instagram')"
                  />
                </div>
              </AccordionItem>
            </Accordion>

            <a
              href="https://explore.approachguides.com/x/minute-marketeer-generate-leads-with-instagram-ojk35"
              target="_blank"
              class="flex items-center space-x-3 border-t pt-3 text-sm"
            >
              <Icon
                icon="youtube"
                prefix="brands"
              />
              <span>{{ $t('watch_tutorial') }}</span>
            </a>
          </div>
        </ShareStep>
      </ShareSteps>
    </template>

    <template #post>
      <ShareSteps>
        <ShareStep
          :number="1"
          :title="$t('facebook_copy_post_title')"
          :description="$t('share_ai_assistant_intro')"
        >
          <ShareAiDraftPanel
            platform="facebook-conversation"
            :ai-endpoint="aiEndpoint"
            :ai-body="aiBody"
            :language="language"
            :landing-page-url="landingPageUrl"
            :partner-name="partnerName"
            :copy-label="$t('copy_post')"
            open-href="https://www.instagram.com"
            :open-label="$t('open_instagram')"
            hide-intro
          />
        </ShareStep>

        <ShareStep
          :number="2"
          :title="$t('instagram_download_image_title')"
          :description="$t('instagram_download_image_description')"
        >
          <ShareImageDownload
            :media="media"
            :is-loading="isMediaLoading"
            preferred-aspect-ratio="square"
          />
        </ShareStep>

        <ShareStep
          :number="3"
          :title="$t('facebook_copy_link_title')"
          :description="$t('facebook_copy_link_description')"
        >
          <ShareCopyLink
            :landing-page-url="landingPageUrl"
            open-href="https://www.instagram.com"
            :open-label="$t('open_instagram')"
          />
        </ShareStep>
      </ShareSteps>
    </template>
  </ShareModes>
</template>

<script setup lang="ts">
import type { AiGenerationStatus } from '~/types/ai'
import type { LandingPage } from '~/types/landing-page'
import type { ShareMode } from '~/types/share'

defineProps<{
  aiEndpoint: string
  aiBody?: Record<string, unknown>
  language: string
  landingPageUrl: string
  partnerName?: string
  initialText?: string | null
  media?: LandingPage['media'] | null
  isMediaLoading?: boolean
}>()

const { t } = useI18n()

const modes: ShareMode[] = [
  {
    value: 'story',
    icon: 'rectangle-vertical-history',
    title: t('instagram_create_story_title'),
    description: t('instagram_create_story_description'),
    features: [
      t('instagram_create_story_feature_1'),
      t('instagram_create_story_feature_2'),
      t('instagram_create_story_feature_3')
    ],
    classes: {
      card: 'hover:border-pink-300 hover:bg-pink-50/50',
      icon: 'bg-pink-50 text-pink-500',
      accent: 'text-pink-500'
    }
  },
  {
    value: 'post',
    icon: 'image',
    title: t('instagram_create_post_title'),
    description: t('instagram_create_post_description'),
    features: [
      t('instagram_create_post_feature_1'),
      t('instagram_create_post_feature_2'),
      t('instagram_create_post_feature_3')
    ],
    classes: {
      card: 'hover:border-violet-300 hover:bg-violet-50/50',
      icon: 'bg-violet-50 text-violet-600',
      accent: 'text-violet-600'
    }
  }
]

const accordion = ref<number | null>(null)

// Caption
const caption = ref('')
const captionStatus = ref<AiGenerationStatus>('idle')

// Sticker text
const stickerText = ref('')
const stickerTextStatus = ref<AiGenerationStatus>('idle')
</script>

<style>
.share-modal .instagram-section {
  .accordion--divided .accordion-item .accordion-item__header {
    @apply px-0 py-3;
  }

  .accordion--divided .accordion-item:first-child .accordion-item__header {
    @apply pt-0;
  }

  .accordion--divided .accordion-item--active .accordion-item__content {
    @apply border-t-0 px-0 py-6;
  }
}
</style>
